import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase';
// Added API health check (silent - no UI changes)
import apiClient from '../services/api.js';
import './Login.css';

const goaPoliceLogo = '/Emblem_of_Goa_Police.png';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [apiStatus, setApiStatus] = useState({ available: true, checked: false }); // Default to available

  const googleProvider = useMemo(() => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
  }, []);

  // Check API health silently in background
  useEffect(() => {
    checkApiHealthSilently();
  }, []);

  useEffect(() => {
    // Ensure auth persistence is local so refresh keeps the session
    setPersistence(auth, browserLocalPersistence).catch(() => {});

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/home', { replace: true });
      } else {
        setCheckingSession(false);
      }
    });
    return () => unsub();
  }, [navigate]);

  /**
   * Check if backend API is available (silently, no UI changes)
   */
  const checkApiHealthSilently = async () => {
    try {
      console.log('[Login] Checking backend API health silently...');
      const isHealthy = await apiClient.healthCheck();
      setApiStatus({ available: isHealthy, checked: true });
      console.log(`[Login] Backend API status: ${isHealthy ? '✅ Available' : '❌ Unavailable'}`);
    } catch (error) {
      console.error('[Login] Backend API health check failed:', error);
      setApiStatus({ available: false, checked: true });
    }
  };

  const handleLogin = useCallback(async () => {
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email.trim(), password.trim());
      console.log('[Login] ✅ Login successful');
      
      // Check API after successful login (silent)
      if (!apiStatus.checked || !apiStatus.available) {
        console.log('[Login] Rechecking API availability after login...');
        checkApiHealthSilently();
      }
    } catch (err) {
      console.error('[Login] ❌ Login error:', err);
      
      if (err?.code === 'auth/invalid-credential' || err?.code === 'auth/wrong-password' || err?.code === 'auth/user-not-found') {
        setError('Invalid email or password. Please check your credentials.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err?.code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(err.message || 'An error occurred during login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [email, password, apiStatus]);

  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      await signInWithPopup(auth, googleProvider);
      console.log('[Login] ✅ Google login successful');
      
      // Check API after successful login (silent)
      if (!apiStatus.checked || !apiStatus.available) {
        console.log('[Login] Rechecking API availability after login...');
        checkApiHealthSilently();
      }
    } catch (err) {
      console.error('[Login] ❌ Google login error:', err);
      setError(err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [googleProvider, apiStatus]);

  // Show loading spinner while checking session
  if (checkingSession) {
    return (
      <div className="login-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {/* Left Panel - Login Form */}
      <div className="login-panel">
        <div className="login-content">
          <div className="login-header">
            <img src={goaPoliceLogo} alt="Goa Police" className="police-logo" />
            <h1>Officer Login</h1>
          </div>

          <form className="login-form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <div className="form-group">
              <label htmlFor="email">Email ID</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={loading}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your secure password"
                required
                disabled={loading}
                className="form-input"
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="secure-login-btn"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Secure Login'}
            </button>

            <div className="divider">
              <span>OR</span>
            </div>

            <button
              type="button"
              className="google-login-btn"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </form>

          <div className="login-footer">
            <p>New officer? <a href="/signup" className="register-link">Register here</a></p>
            <p>For password recovery, contact IT support.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="branding-panel">
        <div className="branding-content">
          <h2>Cyber Vigil</h2>
          <p>Monitor and analyze cyber threats across platforms.</p>
        </div>
      </div>
    </div>
  );
}
